import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Button, Select, MenuItem,
  FormControl, InputLabel, Chip, Divider, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Alert,
  TextField, InputAdornment, ToggleButton, ToggleButtonGroup,
  TablePagination,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import TableChartIcon from '@mui/icons-material/TableChart';
import api from '../../services/api';

interface BcnReport {
  meta: {
    gerado_em: string;
    periodo: { inicio: string; fim: string; descricao: string };
    plataforma: string;
    destinatario: string;
  };
  entidades: {
    total: number; activas: number; novas_periodo: number;
    por_tipo: { tipo: number; total: number }[];
  };
  utilizadores: {
    total: number; activos: number; novos_periodo: number;
    email_verificado: number; perfil_completo: number;
    crescimento_mensal: { mes: string; novos: number }[];
  };
  pagamentos_sisp: {
    total_tentativas: number; pagamentos_sucesso: number; pagamentos_falhados: number;
    em_processamento: number; volume_sucesso_cve: number; taxas_cobradas_cve: number;
    ticket_medio_cve: number; entidades_com_vendas: number; utilizadores_compradores: number;
    taxa_sucesso_pct: number;
    por_mes: { mes: string; sucesso: number; falhados: number; volume_cve: number }[];
    por_tipo: { tipo: string; total: number; volume_cve: number }[];
  };
  wallet_pagali: {
    wallets_activas: number; num_carregamentos: number; num_pagamentos: number;
    num_transferencias: number; volume_carregamentos_cve: number;
    volume_pagamentos_cve: number; volume_transferencias_cve: number;
    volume_cashback_cve: number; saldo_total_circulacao_cve: number;
    total_wallets_emitidas: number;
  };
  cashless_nfc: {
    total_transacoes: number; pagamentos: number; carregamentos: number;
    reversoes: number; volume_pagamentos_cve: number; volume_carregamentos_cve: number;
    wallets_nfc_activas: number; comerciantes_nfc: number;
    pulseiras_total: number; pulseiras_activas: number;
  };
  gateway_geral: {
    total_transacoes: number; sucesso: number; volume_cve: number;
    comerciantes: number; utilizadores: number;
  };
}

const fmt = (n: number) => n?.toLocaleString('pt-PT', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtCve = (n: number) => `${fmt(n)} CVE`;
const fmtPct = (n: number) => `${n?.toFixed(1)}%`;

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Paper sx={{ p: 2.5, height: '100%' }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>{label}</Typography>
      <Typography variant="h5" fontWeight={700}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Box mt={4} mb={2}>
      <Typography variant="h6" fontWeight={700} color="primary">{children}</Typography>
      <Divider sx={{ mt: 0.5 }} />
    </Box>
  );
}

const PERIODS = [
  { value: 'current', label: 'Mês corrente' },
  { value: 'ytd',     label: `${new Date().getFullYear()} — Acumulado` },
  { value: String(new Date().getFullYear()), label: `Ano ${new Date().getFullYear()}` },
  { value: String(new Date().getFullYear() - 1), label: `Ano ${new Date().getFullYear() - 1}` },
  { value: 'all',     label: 'Histórico completo' },
];

interface EntityRow {
  id: string; nome: string; nif: string; email: string; telefone: string;
  morada: string; estado: string; tipo_entidade: string; entidade_sisp: string;
  iban_bcn_verificado: string; vouchers_activos: string;
  num_servicos: number; servicos: string; data_registo: string;
}

export default function BcnReport() {
  const [period, setPeriod] = useState('current');
  const [report, setReport] = useState<BcnReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Entidades
  const [entities, setEntities] = useState<EntityRow[]>([]);
  const [entLoading, setEntLoading] = useState(false);
  const [entEstado, setEntEstado] = useState<'A' | 'I' | 'all'>('A');
  const [entSearch, setEntSearch] = useState('');
  const [entPage, setEntPage] = useState(0);
  const [entRowsPerPage, setEntRowsPerPage] = useState(25);

  const loadEntities = useCallback(async (estado: string) => {
    setEntLoading(true);
    try {
      const res = await api.get(`/admin/reports/bcn/entities?estado=${estado}`);
      setEntities(res.data.data ?? []);
    } catch {
      setEntities([]);
    } finally {
      setEntLoading(false);
    }
  }, []);

  useEffect(() => { loadEntities(entEstado); }, [entEstado, loadEntities]);

  const filteredEntities = entities.filter(e =>
    !entSearch || [e.nome, e.nif, e.email, e.servicos, e.tipo_entidade]
      .some(v => v?.toLowerCase().includes(entSearch.toLowerCase()))
  );

  const downloadEntitiesXlsx = async () => {
    try {
      const res = await api.get(
        `/admin/reports/bcn/entities?estado=${entEstado}&format=xlsx`,
        { responseType: 'blob' }
      );
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = 'entidades_pagali.xlsx';
      a.click();
    } catch {
      alert('Erro ao exportar Excel');
    }
  };

  const generate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/admin/reports/bcn?period=${period}`);
      setReport(res.data.report);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  }, [period]);

  const downloadCsv = () => {
    const url = `${(api.defaults.baseURL || '').replace('/v1','')}/v1/admin/reports/bcn?period=${period}&format=csv`;
    window.open(url, '_blank');
  };

  return (
    <Box p={3} maxWidth={1100}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Relatório BCN</Typography>
          <Typography variant="body2" color="text.secondary">
            Dados da plataforma Pagali para envio ao Banco Comercial do Atlântico
          </Typography>
        </Box>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Período</InputLabel>
            <Select value={period} label="Período" onChange={e => setPeriod(e.target.value)}>
              {PERIODS.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={generate} disabled={loading}>
            Gerar
          </Button>
          {report && (
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadCsv}>
              CSV
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!report && !loading && (
        <Paper sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
          <Typography>Selecciona o período e clica em <strong>Gerar</strong> para produzir o relatório.</Typography>
        </Paper>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      )}

      {report && !loading && (
        <>
          {/* Meta */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
            <Typography variant="body2">
              <strong>Período:</strong> {report.meta.periodo.inicio} → {report.meta.periodo.fim} ({report.meta.periodo.descricao})
              &nbsp;·&nbsp;
              <strong>Gerado em:</strong> {new Date(report.meta.gerado_em).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}
              &nbsp;·&nbsp;
              <strong>Destinatário:</strong> {report.meta.destinatario}
            </Typography>
          </Paper>

          {/* Entidades */}
          <SectionTitle>1. Entidades Registadas</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}><StatCard label="Total entidades" value={fmt(report.entidades.total)} /></Grid>
            <Grid item xs={12} sm={4}><StatCard label="Activas" value={fmt(report.entidades.activas)} sub={fmtPct(report.entidades.activas / (report.entidades.total || 1) * 100) + ' do total'} /></Grid>
            <Grid item xs={12} sm={4}><StatCard label="Novas no período" value={fmt(report.entidades.novas_periodo)} /></Grid>
          </Grid>

          {/* Utilizadores */}
          <SectionTitle>2. Utilizadores</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}><StatCard label="Total users" value={fmt(report.utilizadores.total)} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Activos" value={fmt(report.utilizadores.activos)} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Novos no período" value={fmt(report.utilizadores.novos_periodo)} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Email verificado" value={fmt(report.utilizadores.email_verificado)} /></Grid>
          </Grid>
          {report.utilizadores.crescimento_mensal.length > 0 && (
            <Box mt={2} overflow="auto">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Mês</TableCell>
                    <TableCell align="right">Novos utilizadores</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.utilizadores.crescimento_mensal.map(r => (
                    <TableRow key={r.mes}>
                      <TableCell>{r.mes}</TableCell>
                      <TableCell align="right">{fmt(r.novos)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Pagamentos SISP */}
          <SectionTitle>3. Pagamentos SISP / VINTI4</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}><StatCard label="Total tentativas" value={fmt(report.pagamentos_sisp.total_tentativas)} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Sucesso" value={fmt(report.pagamentos_sisp.pagamentos_sucesso)} sub={`Taxa: ${fmtPct(report.pagamentos_sisp.taxa_sucesso_pct)}`} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Volume (CVE)" value={fmtCve(report.pagamentos_sisp.volume_sucesso_cve)} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Ticket médio" value={fmtCve(report.pagamentos_sisp.ticket_medio_cve)} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Taxas cobradas" value={fmtCve(report.pagamentos_sisp.taxas_cobradas_cve)} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Entidades vendedoras" value={fmt(report.pagamentos_sisp.entidades_com_vendas)} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Compradores únicos" value={fmt(report.pagamentos_sisp.utilizadores_compradores)} /></Grid>
            <Grid item xs={6} sm={3}>
              <StatCard label="Estado"
                value={`${fmt(report.pagamentos_sisp.pagamentos_falhados)} falhados`}
                sub={`${fmt(report.pagamentos_sisp.em_processamento)} em processamento`} />
            </Grid>
          </Grid>
          {report.pagamentos_sisp.por_mes.length > 0 && (
            <Box mt={2} overflow="auto">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Mês</TableCell>
                    <TableCell align="right">Sucesso</TableCell>
                    <TableCell align="right">Falhados</TableCell>
                    <TableCell align="right">Volume CVE</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.pagamentos_sisp.por_mes.map(r => (
                    <TableRow key={r.mes}>
                      <TableCell>{r.mes}</TableCell>
                      <TableCell align="right"><Chip label={fmt(r.sucesso)} size="small" color="success" /></TableCell>
                      <TableCell align="right"><Chip label={fmt(r.falhados)} size="small" color="error" /></TableCell>
                      <TableCell align="right">{fmtCve(r.volume_cve)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Wallet */}
          <SectionTitle>4. Wallet Pagali</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}><StatCard label="Wallets emitidas" value={fmt(report.wallet_pagali.total_wallets_emitidas)} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Saldo em circulação" value={fmtCve(report.wallet_pagali.saldo_total_circulacao_cve)} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Carregamentos" value={fmt(report.wallet_pagali.num_carregamentos)} sub={fmtCve(report.wallet_pagali.volume_carregamentos_cve)} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Pagamentos" value={fmt(report.wallet_pagali.num_pagamentos)} sub={fmtCve(report.wallet_pagali.volume_pagamentos_cve)} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Transferências" value={fmt(report.wallet_pagali.num_transferencias)} sub={fmtCve(report.wallet_pagali.volume_transferencias_cve)} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Cashback emitido" value={fmtCve(report.wallet_pagali.volume_cashback_cve)} /></Grid>
          </Grid>

          {/* NFC */}
          <SectionTitle>5. Cashless NFC (EventsCV)</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}><StatCard label="Total transacções" value={fmt(report.cashless_nfc.total_transacoes)} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Pagamentos NFC" value={fmt(report.cashless_nfc.pagamentos)} sub={fmtCve(report.cashless_nfc.volume_pagamentos_cve)} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Pulseiras activas" value={fmt(report.cashless_nfc.pulseiras_activas)} sub={`de ${fmt(report.cashless_nfc.pulseiras_total)} emitidas`} /></Grid>
            <Grid item xs={6} sm={3}><StatCard label="Comerciantes NFC" value={fmt(report.cashless_nfc.comerciantes_nfc)} /></Grid>
          </Grid>

          {/* Gateway geral */}
          {report.gateway_geral.total_transacoes > 0 && (
            <>
              <SectionTitle>6. Gateway de Pagamento (Geral)</SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}><StatCard label="Total transacções" value={fmt(report.gateway_geral.total_transacoes)} /></Grid>
                <Grid item xs={6} sm={3}><StatCard label="Sucesso" value={fmt(report.gateway_geral.sucesso)} /></Grid>
                <Grid item xs={6} sm={3}><StatCard label="Volume" value={fmtCve(report.gateway_geral.volume_cve)} /></Grid>
                <Grid item xs={6} sm={3}><StatCard label="Comerciantes" value={fmt(report.gateway_geral.comerciantes)} /></Grid>
              </Grid>
            </>
          )}

          <Box mt={4} display="flex" justifyContent="flex-end">
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={downloadCsv} size="large">
              Exportar CSV para BCN
            </Button>
          </Box>
        </>
      )}

      {/* ── Listagem de Entidades (sempre visível) ────────────────────── */}
      <SectionTitle>Entidades Registadas no Pagali</SectionTitle>

      <Box display="flex" gap={2} alignItems="center" mb={2} flexWrap="wrap">
        <ToggleButtonGroup
          size="small" value={entEstado} exclusive
          onChange={(_, v) => { if (v) { setEntEstado(v); setEntPage(0); } }}
        >
          <ToggleButton value="A">Activas</ToggleButton>
          <ToggleButton value="I">Inactivas</ToggleButton>
          <ToggleButton value="all">Todas</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          size="small" placeholder="Pesquisar por nome, NIF, serviço…"
          value={entSearch} onChange={e => { setEntSearch(e.target.value); setEntPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 280 }}
        />

        <Box flex={1} />

        <Typography variant="body2" color="text.secondary">
          {filteredEntities.length} entidade{filteredEntities.length !== 1 ? 's' : ''}
        </Typography>

        <Button
          variant="contained" color="success"
          startIcon={<TableChartIcon />}
          onClick={downloadEntitiesXlsx}
          disabled={entLoading}
        >
          Exportar Excel
        </Button>
      </Box>

      <Paper>
        {entLoading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : (
          <>
            <Box overflow="auto">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'action.hover' } }}>
                    <TableCell>Nome</TableCell>
                    <TableCell>NIF</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Telefone</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>IBAN BCN</TableCell>
                    <TableCell>Nº Serv.</TableCell>
                    <TableCell>Serviços</TableCell>
                    <TableCell>Registo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEntities
                    .slice(entPage * entRowsPerPage, entPage * entRowsPerPage + entRowsPerPage)
                    .map(e => (
                      <TableRow key={e.id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{e.nome}</TableCell>
                        <TableCell>{e.nif || '—'}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{e.email || '—'}</TableCell>
                        <TableCell>{e.telefone || '—'}</TableCell>
                        <TableCell><Chip label={e.tipo_entidade || '—'} size="small" /></TableCell>
                        <TableCell>
                          <Chip
                            label={e.estado === 'A' ? 'Activa' : 'Inactiva'}
                            size="small"
                            color={e.estado === 'A' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={e.iban_bcn_verificado}
                            size="small"
                            color={e.iban_bcn_verificado === 'Sim' ? 'info' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">{e.num_servicos}</TableCell>
                        <TableCell sx={{ fontSize: 11, maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {e.servicos}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                          {e.data_registo ? new Date(e.data_registo).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  {filteredEntities.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        Nenhuma entidade encontrada
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
            <TablePagination
              component="div"
              count={filteredEntities.length}
              page={entPage}
              onPageChange={(_, p) => setEntPage(p)}
              rowsPerPage={entRowsPerPage}
              onRowsPerPageChange={e => { setEntRowsPerPage(parseInt(e.target.value)); setEntPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
